import React, { useState, useEffect } from "react";

const StateDropdown = ({ 
  countryCode,
  value, 
  onChange, 
  placeholder = "Select State",
  className = "",
  required = false,
  id = "state",
  name = "state"
}) => {
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (countryCode) {
      fetchStates(countryCode);
    } else {
      setStates([]);
    }
  }, [countryCode]);

  const fetchStates = async (code) => {
    try {
      setLoading(true);
      setError(null);

      // The states endpoint expects a country NAME, but this component receives
      // an ISO2 code. restcountries.com (which we previously used to convert
      // code -> name) now 301-redirects to a non-CORS host, so resolve the name
      // from countriesnow's CORS-enabled /iso list instead.
      const isoRes = await fetch(
        'https://countriesnow.space/api/v0.1/countries/iso'
      );
      if (!isoRes.ok) {
        throw new Error(`Failed to resolve country: ${isoRes.status}`);
      }
      const isoJson = await isoRes.json();
      const match = (isoJson.data || []).find(
        (c) => (c.Iso2 || '').toUpperCase() === String(code).toUpperCase()
      );
      const countryName = match?.name;
      if (!countryName) {
        // Unknown code — no states to load, but not an error state.
        setStates([]);
        return;
      }

      // countriesnow now serves states via GET /states/q?country=<name>.
      // The old POST /states (JSON body) 301-redirects here, and the browser
      // drops the redirected cross-origin POST -> "Failed to load states".
      const statesResponse = await fetch(
        'https://countriesnow.space/api/v0.1/countries/states/q?country=' +
          encodeURIComponent(countryName)
      );

      if (!statesResponse.ok) {
        throw new Error('Failed to fetch states');
      }

      const statesData = await statesResponse.json();

      if (statesData.data && statesData.data.states) {
        const formattedStates = statesData.data.states
          .map((state) => ({
            name: state.name,
            code: state.state_code || '',
          }))
          .sort((a, b) => a.name.localeCompare(b.name));

        setStates(formattedStates);
      } else {
        setStates([]);
      }
    } catch (err) {
      console.error('Error fetching states:', err);
      setError('Failed to load states');
      setStates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    if (onChange) {
      onChange(e);
    }
  };

  if (!countryCode) {
    return (
      <select 
        className={className}
        disabled
        id={id}
        name={name}
      >
        <option>Please select a country first</option>
      </select>
    );
  }

  if (loading) {
    return (
      <select 
        className={className}
        disabled
        id={id}
        name={name}
      >
        <option>Loading states...</option>
      </select>
    );
  }

  if (error) {
    return (
      <select 
        className={className}
        disabled
        id={id}
        name={name}
      >
        <option>Error loading states</option>
      </select>
    );
  }

  if (states.length === 0) {
    return (
      <select 
        className={className}
        id={id}
        name={name}
        value={value}
        onChange={handleChange}
        required={required}
      >
        <option value="">No states available</option>
      </select>
    );
  }

  return (
    <select
      id={id}
      name={name}
      value={value}
      onChange={handleChange}
      className={className}
      required={required}
      style={{ 
        height: '36px',
        padding: '0 8px',
        lineHeight: '36px'
      }}
    >
      {placeholder && !value && (
        <option value="">
          {placeholder}
        </option>
      )}
      {states.map((state, index) => (
        <option key={state.code || index} value={state.name} style={{ padding: '10px 8px', lineHeight: '1.5' }}>
          {state.name}
        </option>
      ))}
    </select>
  );
};

export default StateDropdown;
